import { useCallback, useMemo } from 'react';
import { useCommandPaletteContext } from '../CommandPaletteContext';

/**
 * Return value of usePluginNavigation
 */
export interface UsePluginNavigationReturn {
  /**
   * Navigate to a specific page within this plugin.
   * Clears search and shows only this page's commands.
   * Optionally override the placeholder text for the duration of the page.
   */
  navigateTo: (pageId: string, placeholder?: string) => void;

  /**
   * Return to global mode, showing all plugins' global commands.
   */
  navigateBack: () => void;

  /**
   * Open the palette and navigate to a specific page in one step.
   * Useful for keyboard shortcuts that should jump straight to a page.
   * Optionally override the placeholder text for the duration of the page.
   */
  openAndNavigate: (pageId: string, placeholder?: string) => void;

  /**
   * Returns true if the palette is currently showing the given page of this plugin.
   */
  isOnPage: (pageId: string) => boolean;

  /**
   * True if the palette is currently on any page belonging to this plugin.
   */
  isActive: boolean;
}

/**
 * Hook for plugin authors to interact with the page navigation system.
 *
 * Scopes all navigation calls to the given `pluginId` so the plugin doesn't
 * need to repeat its own ID in every call.
 *
 * @example
 * ```tsx
 * const PLUGIN_ID = 'my-plugin';
 *
 * export const MyPlugin: React.FC = () => {
 *   const { navigateTo, isOnPage } = usePluginNavigation(PLUGIN_ID);
 *
 *   const globalCommands = useMemo(() => [{
 *     id: 'open-items',
 *     label: 'Browse Items',
 *     category: 'My Plugin',
 *     primary: { id: 'action', label: 'Browse', action: () => navigateTo('items') },
 *   }], [navigateTo]);
 *
 *   const itemCommands = useMemo(() => [...], []);
 *
 *   useRegisterCommands({ pluginId: PLUGIN_ID, commands: globalCommands });
 *   useRegisterPage({ pluginId: PLUGIN_ID, pageId: 'items', commands: itemCommands });
 *
 *   return null;
 * };
 * ```
 */
export function usePluginNavigation(pluginId: string): UsePluginNavigationReturn {
  const { currentPage, navigateTo, navigateBack, open } = useCommandPaletteContext();

  const navigateToPage = useCallback((pageId: string, placeholder?: string) => {
    navigateTo(pluginId, pageId, placeholder);
  }, [navigateTo, pluginId]);

  const openAndNavigate = useCallback((pageId: string, placeholder?: string) => {
    open();
    navigateTo(pluginId, pageId, placeholder);
  }, [open, navigateTo, pluginId]);

  const isOnPage = useCallback((pageId: string): boolean => {
    return currentPage?.pluginId === pluginId && currentPage?.pageId === pageId;
  }, [currentPage, pluginId]);

  const isActive = useMemo((): boolean => {
    return currentPage?.pluginId === pluginId;
  }, [currentPage, pluginId]);

  return {
    navigateTo: navigateToPage,
    navigateBack,
    openAndNavigate,
    isOnPage,
    isActive,
  };
}
